<template>
    <QuestionBox 
        :title="step.question" 
        :text="correctImages.length === 1 
            ? $t('module.multipleChoiceImageTextSingle') 
            : $t('module.multipleChoiceImageTextMultiple')" 
    />
    <ul class="m-imagePicker u-grid u-listUnstyled" :class="{ '-selected': selectedImages ? selectedImages.length > 0 : false }">
        <li v-for="(image, index) in step.images" :key="index">
            <label>
                <input 
                    :type="correctImages.length === 1 ? 'radio' : 'checkbox'" 
                    :value="image.image" 
                    v-model="selectedImages" 
                    :disabled="isChecking || (correctImages.length > 1 && selectedImages.length >= correctImages.length && !selectedImages.includes(image.image))"
                    @click="handleSelection(image.image)"
                    :class="{
                        '-correct': isChecking && (Array.isArray(selectedImages)
                            ? selectedImages.includes(image.image) && correctImages.includes(image.image)
                            : selectedImages === image.image && correctImages.includes(image.image)),
                        '-incorrect': isChecking && (Array.isArray(selectedImages)
                            ? selectedImages.includes(image.image) && !correctImages.includes(image.image)
                            : selectedImages === image.image && !correctImages.includes(image.image))
                    }"
                />
                <div class="m-imagePicker__item u-br">
                    <img class="m-imagePicker__image u-cover" :src="image.image">
                </div>
            </label>
        </li>
    </ul>
    <FixedButton 
        :isDisabled="!selectedImages || (Array.isArray(selectedImages) && selectedImages.length === 0)" 
        @click="handleButtonClick" 
        :buttonClass="showTryAgain ? '-reset' : ''">
        {{ showTryAgain ? $t('module.tryAgain') : (answeredCorrect ? $t('module.nextQuestion') : $t('module.checkQuestion')) }}
    </FixedButton>
</template>

<script setup>
import { inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { addAttempt } from '../../utils/globalFunctions';

// Components
import QuestionBox from './QuestionBox.vue';
import FixedButton from './FixedButton.vue';

const props = defineProps({
    step: {
        type: Object,
    },
});

const { t } = useI18n(); 

const correctImages = (props.step.images || []).filter(image => image.correct).map(image => image.image);

const selectedImages = ref(correctImages.length === 1 ? null : []);
const showTryAgain = ref(false);
const answeredCorrect = ref(false);
const isChecking = ref(false);

const completeStep = inject('completeStep');
const showNotice = inject('showNotice');
const hideNotice = inject('hideNotice');

const attempts = defineModel('attempts');

const resetQuestion = () => {
    selectedImages.value = correctImages.length === 1 ? null : [];
    showTryAgain.value = false;
    isChecking.value = false;
    hideNotice();
};

const checkAnswer = () => {
    if (!selectedImages.value || (Array.isArray(selectedImages.value) && selectedImages.value.length === 0)) {
        return;
    }

    isChecking.value = true;
    let correctCount = 0;
    let selectedCount = 0;

    if (Array.isArray(selectedImages.value)) {
        correctCount = selectedImages.value.filter(image => correctImages.includes(image)).length;
        selectedCount = selectedImages.value.length;
    } else {
        correctCount = correctImages.includes(selectedImages.value) ? 1 : 0;
        selectedCount = 1;
    }

    if (correctCount === selectedCount && correctCount === correctImages.length) {
        // All correct
        attempts.value.push(addAttempt(props.step.uuid, true));
        answeredCorrect.value = true;
        completeStep(props.step.uuid);
        const textCorrect = props.step.feedback_correct ? props.step.feedback_correct : t('module.correctAnswerText');
        showNotice(t('module.correctAnswerTitle'), textCorrect, '-success');
    } else if (correctCount === 0) {
        // All incorrect
        attempts.value.push(addAttempt(props.step.uuid, false));
        showNotice(
            t('module.wrongImageAnswerTitle'), 
            correctCount === 0 
                ? t('module.wrongImageAnswerText') 
                : t('module.wrongImageAnswerTextMultiple', { n: correctImages.length }), 
            '-error'
        );
        showTryAgain.value = true; 
    } else {
        // Partially correct
        attempts.value.push(addAttempt(props.step.uuid, false));
        showNotice(
            t('module.partiallyWrongImageAnswerTitle'), 
            t('module.partiallyWrongImageAnswerText', { 
                selected: correctCount,
                total: correctImages.length 
            }), 
            '-error'
        );
        showTryAgain.value = true; 
    }
};

const handleButtonClick = () => {
    if (showTryAgain.value) {
        resetQuestion();
    } else if (answeredCorrect.value) {
        completeStep(props.step.uuid, true, false);
        hideNotice();
    } else {
        checkAnswer();
    }
};

const handleSelection = (image) => {
    if (correctImages.length === 1) {
        selectedImages.value = image; // For radio, set the single selected value
    } else if (selectedImages.value.includes(image)) {
        selectedImages.value = selectedImages.value.filter(img => img !== image);
    } else if (selectedImages.value.length < correctImages.length) {
        selectedImages.value.push(image);
    }
};
</script>
